import React from 'react';

import { LinkOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { useFormik } from 'formik';

import { useUpsertWebhookConfig } from '../api/upsertWebhookConfig';
import { useUpsertWebhookCredentials } from '../api/upsertWebhookCredentials';
import { buildWebhookFormSchema } from '../schemas/webhookFormSchema';
import { WEBHOOK_AUTH_TYPE } from '../types';
import { stripIrrelevantAuthFields } from '../webhookFormHelpers';
import { IWebhookFormValues, WebhookForm } from './WebhookForm';
import CancelSubmitFooterModal from '@/components/CancelSubmitFooterModal';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

export const MODAL_WIDTH = {
  xs: '100%',
  sm: '80%',
  md: '70%',
  lg: '60%',
  xl: '50%',
};

interface IAddWebhookModalProps {
  partnerId: string;
  open: boolean;
  staticConfig: string[];
  onClose: () => void;
  onSuccess?: (configId: string) => void;
}

export const AddWebhookModal = ({
  partnerId,
  open,
  staticConfig,
  onClose,
  onSuccess,
}: IAddWebhookModalProps) => {
  const addToast = useToasts((state) => state.addItem);

  const { mutateAsync: upsertWebhookConfig, isPending: isPendingConfig } =
    useUpsertWebhookConfig({ partnerId });

  const {
    mutateAsync: upsertWebhookCredentials,
    isPending: isPendingCredentials,
  } = useUpsertWebhookCredentials({ partnerId });

  const isPending = isPendingConfig || isPendingCredentials;

  const validationSchema = buildWebhookFormSchema();

  const formik = useFormik<IWebhookFormValues>({
    initialValues: {
      url: '',
      auth_type: WEBHOOK_AUTH_TYPE.PUBLIC,
      basic_username: '',
      basic_password: '',
      header_name: '',
      header_value: '',
      query_parameter: '',
      query_value: '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const payloads = staticConfig.reduce<Record<string, boolean>>(
          (acc, key) => ({ ...acc, [key]: false }),
          {}
        );
        const { credentials, config } = stripIrrelevantAuthFields(values);
        const newConfig = await upsertWebhookConfig({
          values: { ...config, ...payloads },
        });
        await upsertWebhookCredentials({
          configId: newConfig.config_id,
          values: credentials,
        });
        addToast(
          { msg: 'Webhook URL created', type: MessageType.Success },
          'root',
          {}
        );
        formik.resetForm();
        onClose();

        onSuccess?.(newConfig.config_id);
      } catch (err) {
        addToast(
          {
            msg: err instanceof Error ? err.message : 'Create failed',
            type: MessageType.Error,
          },
          'root',
          {}
        );
      }
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal
      title={
        <span>
          <LinkOutlined /> Add Webhook
        </span>
      }
      open={open}
      destroyOnHidden
      footer={CancelSubmitFooterModal(
        !formik.isValid,
        formik.handleSubmit,
        handleCancel,
        isPending
      )}
      onCancel={handleCancel}
      width={MODAL_WIDTH}
    >
      <WebhookForm formik={formik} />
    </Modal>
  );
};
